import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENCustomProductContextAvailability from "@tCentro/Components/CENCustomProductContextAvailability";
import { ProductAvailabilityStyles } from "@tCentro/Overrides/Components/ProductAvailability";
import ProductContextAvailability from "@tCentro/Overrides/Components/ProductContextAvailability";
import { TYPOGRAPHY } from "@tCentro/theme/design";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    configurationCompleted: state.pages.productDetails.configurationCompleted,
});

export interface ProductDetailsAvailabilityStyles {
    availability?: ProductAvailabilityStyles;
    zoneStyles?: InjectableCss;
    modalStyles?: ModalPresentationProps;
}

export const availabilityStyles: ProductDetailsAvailabilityStyles = {
    availability: {
        infoLinkStyles: {
            css: css`
                font-size: ${TYPOGRAPHY.size.tiny};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: 16px;
                margin-left: 4px;
                color: ${getColor("text.main")};
            `,
        },
        infoIconStyles: {
            size: 12,
            color: "#636161",
            css: css`
                svg {
                    > path {
                        fill: #636161 !important;
                    }
                }
            `,
        },
        container: {
            gap: 0,
            css: css`
                flex-grow: 0;
            `,
        },
    },
    zoneStyles: {
        css: css``,
    },
    modalStyles: {
        cssOverrides: {
            modalTitle: css`
                border-bottom: none !important;
                padding: 0;
                h4 {
                    //styleName: Headings/LG Page & Screen;
                    font-family: Poppins;
                    font-size: 21px;
                    font-weight: 700;
                    line-height: 40px;
                }
                button[data-test-selector^="modalCloseButton"] {
                    position: absolute;
                    right: 16px;
                    top: 16px;

                    span {
                        color: #262525 !important;
                    }
                }
            `,
            modalContent: css`
                padding: 0;
            `,
            modalContainer: css`
                > div {
                    padding: 40px !important;
                }
            `,
        },
    },
};

const styles = availabilityStyles;

const ProductDetailsAvailability = ({ product, configurationCompleted, id }: Props) => {
    const [getFasterModalIsOpen, setGetFasterModalIsOpen] = React.useState(false);
    if (!product) {
        return null;
    }

    const closeModalHandler = () => {
        setGetFasterModalIsOpen(false);
    };

    React.useEffect(() => {
        if (getFasterModalIsOpen) {
            const observer = new MutationObserver(() => {
                const manufacturerInput = document.querySelector<HTMLInputElement>(
                    "input[name='manufacturer_s_part_number']",
                );
                const categoryInput = document.querySelector<HTMLInputElement>("input[name='product_category']");
                const vendorInput = document.querySelector<HTMLInputElement>("input[name='product_vendor']");
                if (manufacturerInput) {
                    manufacturerInput.value = product?.manufacturerItem ?? "Part Number";
                }
                if (categoryInput) {
                    categoryInput.value = product?.properties?.parentCategoryName ?? "Category";
                }
                if (vendorInput) {
                    vendorInput.value = product?.brand?.name ?? "Vendor";
                }
            });

            observer.observe(document.body, { childList: true, subtree: true });

            return () => observer.disconnect(); // Cleanup when modal closes
        }
    }, [getFasterModalIsOpen]);
    return (
        <>
            <CENCustomProductContextAvailability
                isProductDetailsPage={true}
                configurationCompleted={configurationCompleted}
                extendedStyles={styles.availability}
                getFasterModalIsOpen={getFasterModalIsOpen}
                setGetFasterModalIsOpen={setGetFasterModalIsOpen}
            />
            <Modal
                headline={translate("Product available for purchase")}
                isOpen={getFasterModalIsOpen}
                handleClose={closeModalHandler}
                {...styles.modalStyles}
            >
                <StyledWrapper {...styles.zoneStyles}>
                    <Zone contentId={id} zoneName="typicallyShipsWithin_AvailableForPurchase" />
                </StyledWrapper>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsAvailability)),
    definition: {
        displayName: "Availability",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
